<template>
  <div class="act-manual-form">
    <EstudyooLoadingBanner :visible="cargandoEdit" message="Cargando actividad para editar…" />

    <div v-if="isEditMode" class="surface-100 border-round border-1 surface-border p-3 mb-3 flex flex-wrap align-items-center gap-2">
      <i class="pi pi-pencil text-primary" />
      <span class="text-sm flex-1">
        Editando actividad: <strong>{{ act.titulo || editActividadId }}</strong>
      </span>
      <ButtonPrime label="Nueva actividad" icon="pi pi-plus" class="p-button-sm p-button-outlined" @click="iniciarNueva" />
    </div>

    <section v-if="showExcelBar" class="surface-card border-round border-1 surface-border p-3 mb-3">
      <h4 class="text-base font-semibold mt-0 mb-2">Plantillas e importación Excel</h4>
      <p class="text-sm text-600 mb-3">
        Descarga plantillas, completa en Excel e importa actividades completas o solo materiales.
        La plantilla de actividades trae <strong>un ejemplo por cada tipo</strong> (incluye
        <code>datos_json</code> para ordenar palabras, emparejar, etc.) y una hoja de instrucciones.
      </p>
      <EstudyooLoadingBanner
        :visible="importandoFull || importandoMat || descFull || descMat"
        :message="excelBannerMsg"
      />
      <div class="flex flex-wrap gap-2 mb-2">
        <ButtonPrime label="Plantilla actividades" icon="pi pi-download" class="p-button-outlined p-button-sm" :loading="descFull" @click="bajarPlantillaFull" />
        <FileUpload mode="basic" choose-label="Importar actividades" accept=".xlsx,.xls" :auto="true" custom-upload class="p-button-sm" :disabled="importandoFull" @uploader="importarFullExcel" />
        <ButtonPrime label="Plantilla materiales" icon="pi pi-download" class="p-button-outlined p-button-sm" :loading="descMat" @click="bajarPlantillaMat" />
        <FileUpload mode="basic" choose-label="Importar materiales" accept=".xlsx,.xls" :auto="true" custom-upload class="p-button-sm" :disabled="importandoMat" @uploader="importarMatExcel" />
      </div>
    </section>

    <section class="surface-card border-round border-1 surface-border p-3 mb-3">
      <h4 class="text-base font-semibold mt-0 mb-3">1 · Capítulo y asignación</h4>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label class="block text-sm mb-1">Materia <span class="text-red-500">*</span></label>
          <Dropdown v-model="ctx.materia" :options="materias" option-label="nombre" class="w-full" filter @change="onMateriaChange" />
        </div>
        <div>
          <label class="block text-sm mb-1">Nivel</label>
          <Dropdown v-model="ctx.nivel" :options="nivelOpts" option-label="label" option-value="value" class="w-full" show-clear @change="onNivelChange" />
        </div>
        <div>
          <label class="block text-sm mb-1">
            Curso
            <span v-if="asig.publicar !== false" class="text-red-500">*</span>
          </label>
          <Dropdown v-model="ctx.curso" :options="cursoOpts" option-label="label" class="w-full" filter :disabled="!ctx.materia || asig.publicar === false" />
        </div>
        <div>
          <label class="block text-sm mb-1">Capítulo <span class="text-red-500">*</span></label>
          <Dropdown
            v-model="ctx.capitulo"
            :options="capituloOpts"
            option-label="label"
            class="w-full"
            filter
            :disabled="!ctx.materia"
            :loading="loadingCap"
          />
          <p v-if="ctx.capitulo?.codigo" class="text-xs text-500 mt-1 mb-0">Código: {{ ctx.capitulo.codigo }}</p>
        </div>
        <div>
          <label class="block text-sm mb-1">Tipo de actividad <span class="text-red-500">*</span></label>
          <Dropdown v-model="ctx.tipo" :options="tipos" option-label="nombre" class="w-full" filter />
        </div>
        <div>
          <label class="block text-sm mb-1">Casa (opcional)</label>
          <Dropdown v-model="ctx.casa" :options="casaOpts" option-label="label" class="w-full" show-clear />
        </div>
      </div>
    </section>

    <section class="surface-card border-round border-1 surface-border p-3 mb-3">
      <h4 class="text-base font-semibold mt-0 mb-3">2 · Datos de la actividad</h4>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div class="md:col-span-2">
          <label class="block text-sm mb-1">Título <span class="text-red-500">*</span></label>
          <InputText v-model="act.titulo" class="w-full" />
        </div>
        <div>
          <label class="block text-sm mb-1">Subtítulo</label>
          <InputText v-model="act.subtitulo" class="w-full" />
        </div>
        <div>
          <label class="block text-sm mb-1">Modo</label>
          <Dropdown v-model="act.modo" :options="modoOpts" class="w-full" />
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm mb-1">Instrucciones</label>
          <Textarea v-model="act.instrucciones" rows="3" class="w-full" auto-resize />
        </div>
        <div class="flex align-items-center gap-2">
          <CheckBox v-model="act.activa" input-id="act-activa" :binary="true" />
          <label for="act-activa" class="text-sm cursor-pointer">Actividad activa</label>
        </div>
        <div class="flex align-items-center gap-2">
          <CheckBox v-model="asig.activa" input-id="asig-activa" :binary="true" :disabled="!editAsignacionId && asig.publicar === false" />
          <label for="asig-activa" class="text-sm cursor-pointer">Asignación activa (visible al alumno)</label>
        </div>
        <div class="md:col-span-2 act-manual-form__asig-box border-round border-1 surface-border p-3">
          <div class="flex flex-wrap align-items-center gap-2 mb-2">
            <CheckBox v-model="asig.publicar" input-id="asig-publicar" :binary="true" @change="onAsigPublicarChange" />
            <label for="asig-publicar" class="text-sm cursor-pointer font-semibold">Asignar al curso seleccionado</label>
          </div>
          <div v-if="editAsignacionId" class="flex flex-wrap align-items-center gap-2">
            <span class="text-sm text-green-700">
              <i class="pi pi-check-circle mr-1" />
              Asignada{{ ctx.curso ? ` a ${labelCurso(ctx.curso)}` : '' }}
            </span>
            <ButtonPrime
              label="Quitar asignación"
              icon="pi pi-times"
              class="p-button-sm p-button-danger p-button-outlined"
              :loading="quitandoAsig"
              @click="quitarAsignacion"
            />
          </div>
          <p v-else-if="asig.publicar === false" class="text-xs text-500 m-0">
            La actividad permanece en el catálogo sin publicar al curso.
          </p>
          <p v-else class="text-xs text-500 m-0">
            Al guardar se creará la asignación al curso del capítulo.
          </p>
          <p v-if="totalAsignacionesExtra > 0" class="text-xs text-orange-600 mt-2 mb-0">
            Hay {{ totalAsignacionesExtra + 1 }} asignación(es) en total; aquí gestionas la principal.
          </p>
        </div>
      </div>
    </section>

    <section class="surface-card border-round border-1 surface-border p-3 mb-3">
      <h4 class="text-base font-semibold mt-0 mb-2">Material del capítulo (opcional)</h4>
      <p class="text-sm text-500 mb-3">
        Imagen o guía del capítulo — el alumno la necesita para desbloquear actividades.
        <span v-if="isEditMode" class="text-orange-600"> Al editar, solo se agrega material nuevo (no modifica los existentes).</span>
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label class="block text-sm mb-1">Título del material</label>
          <InputText v-model="matCap.titulo" class="w-full" placeholder="Ej: Guía La geósfera" :disabled="!ctx.capitulo" />
        </div>
        <div class="flex flex-wrap align-items-end gap-2">
          <input ref="matCapFileRef" type="file" accept="image/jpeg,image/png,image/webp,image/gif" class="hidden" @change="onMatCapFile" />
          <ButtonPrime label="Adjuntar imagen" icon="pi pi-image" class="p-button-outlined p-button-sm" :disabled="!ctx.capitulo" @click="matCapFileRef?.click()" />
          <img v-if="matCap.preview" :src="matCap.preview" alt="" class="act-manual-form__thumb" />
        </div>
      </div>
    </section>

    <section class="surface-card border-round border-1 surface-border p-3 mb-3">
      <div class="flex flex-wrap align-items-center gap-2 mb-3">
        <h4 class="text-base font-semibold m-0 flex-1">
          3 · Preguntas
          <span class="text-sm font-normal text-600">({{ preguntas.length }})</span>
        </h4>
      </div>
      <div class="preg-cantidad-bar flex flex-wrap align-items-end gap-2 mb-3 p-3 border-round border-1 surface-border">
        <div>
          <label class="block text-sm mb-1">Cantidad de preguntas</label>
          <InputNumber
            v-model="cantidadPreguntasObjetivo"
            :min="1"
            :max="MAX_PREGUNTAS_ACTIVIDAD"
            :use-grouping="false"
            show-buttons
            button-layout="horizontal"
            class="w-full"
            input-class="w-full"
          />
        </div>
        <ButtonPrime
          label="Aplicar cantidad"
          icon="pi pi-sync"
          class="p-button-sm"
          :disabled="!cantidadPreguntasObjetivo"
          @click="aplicarCantidadPreguntas"
        />
        <ButtonPrime
          label="Agregar una"
          icon="pi pi-plus"
          class="p-button-sm p-button-outlined"
          :disabled="preguntas.length >= MAX_PREGUNTAS_ACTIVIDAD"
          @click="agregarPregunta"
        />
        <p class="text-xs text-500 m-0 flex-1" style="min-width: 12rem">
          Dinámico (1–{{ MAX_PREGUNTAS_ACTIVIDAD }}), igual que en generación con IA.
          «Aplicar» agrega o quita slots vacíos al final para llegar a esa cantidad.
        </p>
      </div>
      <p class="text-xs text-500 mb-3">
        Según el <strong>tipo</strong> aparecen los campos necesarios.
        <strong>Imágenes:</strong>
        alternativas → botón «Adjuntar imagen» en cada opción;
        verdadero/falso → imagen del enunciado;
        imagen↔frase → «Adjuntar» en cada ítem;
        <em>trazo no usa imagen</em> (solo letra/número guía).
        Tras adjuntar, pulsa <strong>Guardar cambios</strong>.
      </p>
      <div v-for="(p, idx) in preguntas" :key="p._key" class="act-manual-form__block mb-3 p-3 border-round border-1 surface-border">
        <div class="flex justify-content-between align-items-center mb-2">
          <span class="text-sm font-semibold">Pregunta {{ idx + 1 }}</span>
          <ButtonPrime icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm" @click="quitarPregunta(idx)" />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm mb-1">Tipo</label>
            <Dropdown
              v-model="p.tipo"
              :options="tipoPreguntaOpts"
              option-label="label"
              option-value="value"
              class="w-full"
              @change="onTipoPreguntaChange(p)"
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Orden</label>
            <InputNumber v-model="p.orden" :min="0" class="w-full" />
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm mb-1">Enunciado</label>
            <Textarea v-model="p.enunciado" rows="2" class="w-full" auto-resize />
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm mb-1">Explicación (feedback)</label>
            <Textarea v-model="p.explicacion" rows="2" class="w-full" auto-resize />
          </div>
        </div>

        <!-- Alternativas -->
        <div v-if="usaAlternativas(p.tipo)" class="mt-3">
          <div class="preg-img-box mb-3">
            <p class="text-sm font-semibold m-0 mb-1">Imágenes de alternativas</p>
            <p class="text-xs text-500 m-0">
              Marca la correcta y, si aplica, adjunta una imagen por opción (JPG/PNG/WebP/GIF, máx. 5&nbsp;MB).
            </p>
          </div>
          <div v-for="(alt, j) in p.alternativas" :key="j" class="alt-row mb-2">
            <RadioButton v-model="p.correctaIdx" :input-id="`alt-${p._key}-${j}`" :value="j" />
            <InputText v-model="alt.texto" class="flex-1" :placeholder="`Alt. ${String.fromCharCode(65 + j)}`" />
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" class="hidden" :ref="(el) => setAltFileRef(p._key, j, el)" @change="(e) => onAltFile(e, p, j)" />
            <ButtonPrime
              :label="alt.preview || alt.file ? 'Cambiar imagen' : 'Adjuntar imagen'"
              icon="pi pi-image"
              class="p-button-outlined p-button-sm"
              @click="triggerAltFile(p._key, j)"
            />
            <img v-if="alt.preview" :src="alt.preview" alt="" class="act-manual-form__thumb act-manual-form__thumb--sm" />
          </div>
          <div v-if="p.tipo === 'image_true_false'" class="preg-img-box mt-3">
            <p class="text-sm font-semibold m-0 mb-2">Imagen del enunciado (opcional)</p>
            <div class="flex flex-wrap align-items-center gap-2 mb-2">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                class="hidden"
                :ref="(el) => setPreguntaFileRef(p._key, el)"
                @change="(e) => onPreguntaImagenFile(e, p)"
              />
              <ButtonPrime
                :label="p.imagenPreview || p.imagenUrl ? 'Cambiar imagen' : 'Adjuntar imagen'"
                icon="pi pi-upload"
                class="p-button-sm p-button-outlined"
                :loading="p._uploadingImg"
                @click="triggerPreguntaFile(p._key)"
              />
              <img v-if="p.imagenPreview || p.imagenUrl" :src="p.imagenPreview || p.imagenUrl" alt="" class="act-manual-form__thumb" />
              <ButtonPrime
                v-if="p.imagenUrl || p.imagenFile"
                icon="pi pi-times"
                class="p-button-text p-button-danger p-button-sm"
                v-tooltip.top="'Quitar imagen'"
                @click="limpiarImagenPregunta(p)"
              />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-sm mb-1">Icono (si no hay imagen)</label>
                <InputText v-model="p.icono" class="w-full" placeholder="help, square, circle…" />
              </div>
              <div>
                <label class="block text-sm mb-1">URL (alternativa a adjuntar)</label>
                <InputText v-model="p.imagenUrl" class="w-full" placeholder="https://…" />
              </div>
            </div>
          </div>
        </div>

        <!-- Ordenar palabras -->
        <div v-else-if="p.tipo === 'order_words'" class="mt-3">
          <p class="text-xs text-orange-700 mb-2">Este tipo no usa imágenes: solo texto de palabras.</p>
          <p class="text-xs text-500 mb-2">
            Escribe las palabras en el <strong>orden correcto</strong>. El alumno las verá mezcladas.
          </p>
          <div class="flex flex-wrap gap-2 mb-2">
            <span v-for="(w, wi) in p.palabras" :key="`${p._key}-w-${wi}`" class="preg-chip">
              <span>{{ wi + 1 }}. {{ w }}</span>
              <button type="button" class="preg-chip__x" aria-label="Quitar" @click="p.palabras.splice(wi, 1)">×</button>
            </span>
          </div>
          <div class="flex flex-wrap gap-2">
            <InputText
              v-model="p.palabraNueva"
              class="flex-1"
              placeholder="Nueva palabra o signo (ej. Hoy, ., ?)"
              @keydown.enter.prevent="agregarPalabra(p)"
            />
            <ButtonPrime label="Agregar" icon="pi pi-plus" class="p-button-sm" @click="agregarPalabra(p)" />
          </div>
        </div>

        <!-- Emparejar columnas -->
        <div v-else-if="p.tipo === 'match_eight'" class="mt-3">
          <p class="text-xs text-orange-700 mb-2">Este tipo no usa imágenes: solo textos en dos columnas.</p>
          <p class="text-xs text-500 mb-2">
            La 1.ª de la izquierda se empareja con la 1.ª de la derecha (igual con el resto).
          </p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label class="block text-sm mb-1 font-semibold">Columna izquierda</label>
              <div v-for="(_, i) in p.matchIzq" :key="`izq-${p._key}-${i}`" class="mb-2">
                <InputText v-model="p.matchIzq[i]" class="w-full" :placeholder="`Izq. ${i + 1}`" />
              </div>
              <ButtonPrime label="Fila" icon="pi pi-plus" class="p-button-text p-button-sm" @click="p.matchIzq.push('')" />
            </div>
            <div>
              <label class="block text-sm mb-1 font-semibold">Columna derecha</label>
              <div v-for="(_, i) in p.matchDer" :key="`der-${p._key}-${i}`" class="mb-2">
                <InputText v-model="p.matchDer[i]" class="w-full" :placeholder="`Der. ${i + 1}`" />
              </div>
              <ButtonPrime label="Fila" icon="pi pi-plus" class="p-button-text p-button-sm" @click="p.matchDer.push('')" />
            </div>
          </div>
        </div>

        <!-- Trazo -->
        <div v-else-if="p.tipo === 'trace_pattern'" class="mt-3">
          <div class="preg-img-box mb-3">
            <p class="text-sm font-semibold m-0 mb-1">Sin carga de imagen</p>
            <p class="text-xs text-500 m-0">
              El player dibuja la guía a partir de la <strong>letra o número</strong> (ej. A, M, 3).
              No se adjunta archivo: el alumno traza encima de esa guía manuscrita.
            </p>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label class="block text-sm mb-1">Letra / número guía</label>
              <InputText v-model="p.trazoGuia" class="w-full" maxlength="2" placeholder="A" />
            </div>
            <div>
              <label class="block text-sm mb-1">Ancho lienzo</label>
              <InputNumber v-model="p.anchoLienzo" :min="120" :max="800" class="w-full" />
            </div>
            <div>
              <label class="block text-sm mb-1">Alto lienzo</label>
              <InputNumber v-model="p.altoLienzo" :min="120" :max="800" class="w-full" />
            </div>
          </div>
        </div>

        <!-- Imagen ↔ frase -->
        <div v-else-if="p.tipo === 'match_images_phrases'" class="mt-3">
          <div class="preg-img-box mb-3">
            <p class="text-sm font-semibold m-0 mb-1">Imágenes del emparejamiento</p>
            <p class="text-xs text-500 m-0">
              Pares por índice (imagen 1 ↔ frase 1). Adjunta archivo o pega URL en cada ítem.
            </p>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label class="block text-sm mb-1 font-semibold">Frases</label>
              <div v-for="(f, i) in p.frases" :key="`f-${p._key}-${i}`" class="mb-2 flex gap-2">
                <InputText v-model="f.texto" class="flex-1" :placeholder="`Frase ${i + 1}`" />
                <ButtonPrime icon="pi pi-times" class="p-button-text p-button-sm p-button-danger" @click="p.frases.splice(i, 1)" />
              </div>
              <ButtonPrime label="Frase" icon="pi pi-plus" class="p-button-text p-button-sm" @click="p.frases.push({ id: `F${p.frases.length + 1}`, texto: '' })" />
            </div>
            <div>
              <label class="block text-sm mb-1 font-semibold">Imágenes</label>
              <div v-for="(im, i) in p.imagenes" :key="`im-${p._key}-${i}`" class="surface-0 border-round border-1 surface-border p-2 mb-2">
                <div class="flex gap-2 mb-2">
                  <InputText v-model="im.etiqueta" class="flex-1" :placeholder="`Etiqueta ${i + 1}`" />
                  <ButtonPrime icon="pi pi-times" class="p-button-text p-button-sm p-button-danger" @click="p.imagenes.splice(i, 1)" />
                </div>
                <div class="flex flex-wrap align-items-center gap-2 mb-2">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    class="hidden"
                    :ref="(el) => setMatchImgFileRef(p._key, i, el)"
                    @change="(e) => onMatchImgFile(e, p, i)"
                  />
                  <ButtonPrime
                    :label="im.preview || im.imagenUrl || im.file ? 'Cambiar imagen' : 'Adjuntar imagen'"
                    icon="pi pi-image"
                    class="p-button-outlined p-button-sm"
                    @click="triggerMatchImgFile(p._key, i)"
                  />
                  <img v-if="im.preview || im.imagenUrl" :src="im.preview || im.imagenUrl" alt="" class="act-manual-form__thumb act-manual-form__thumb--sm" />
                </div>
                <InputText v-model="im.imagenUrl" class="w-full" placeholder="O pega URL de imagen" />
              </div>
              <ButtonPrime
                label="Agregar imagen"
                icon="pi pi-plus"
                class="p-button-text p-button-sm"
                @click="p.imagenes.push({ id: `I${p.imagenes.length + 1}`, etiqueta: '', imagenUrl: '', file: null, preview: '' })"
              />
            </div>
          </div>
        </div>

        <!-- Fracción -->
        <div v-else-if="p.tipo === 'color_fraction'" class="mt-3">
          <p class="text-xs text-orange-700 mb-2">Este tipo no usa imágenes: el alumno colorea una grilla generada.</p>
          <p class="text-xs text-500 mb-2">filas × columnas debe coincidir con el denominador.</p>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label class="block text-sm mb-1">Filas</label>
              <InputNumber v-model="p.filas" :min="1" :max="12" class="w-full" />
            </div>
            <div>
              <label class="block text-sm mb-1">Columnas</label>
              <InputNumber v-model="p.columnas" :min="1" :max="12" class="w-full" />
            </div>
            <div>
              <label class="block text-sm mb-1">Numerador</label>
              <InputNumber v-model="p.fraccionNumerador" :min="0" class="w-full" />
            </div>
            <div>
              <label class="block text-sm mb-1">Denominador</label>
              <InputNumber v-model="p.fraccionDenominador" :min="1" class="w-full" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="surface-card border-round border-1 surface-border p-3 mb-3">
      <div class="flex flex-wrap align-items-center gap-2 mb-3">
        <h4 class="text-base font-semibold m-0 flex-1">4 · Materiales de la actividad</h4>
        <ButtonPrime label="Agregar material" icon="pi pi-plus" class="p-button-sm p-button-outlined" @click="agregarMaterial" />
      </div>
      <div v-for="(mat, idx) in materiales" :key="mat._key" class="act-manual-form__block mb-3 p-3 border-round border-1 surface-border">
        <div class="flex justify-content-between mb-2">
          <span class="text-sm font-semibold">Material {{ idx + 1 }}</span>
          <ButtonPrime icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm" @click="quitarMaterial(idx)" />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm mb-1">Título</label>
            <InputText v-model="mat.titulo" class="w-full" />
          </div>
          <div>
            <label class="block text-sm mb-1">Tipo</label>
            <Dropdown v-model="mat.tipo" :options="tipoMaterialOpts" option-label="label" option-value="value" class="w-full" />
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm mb-1">Contenido / guía</label>
            <Textarea v-model="mat.contenido" rows="2" class="w-full" auto-resize />
          </div>
          <div>
            <label class="block text-sm mb-1">URL externa (opcional)</label>
            <InputText v-model="mat.url" class="w-full" placeholder="https://…" />
          </div>
          <div class="flex flex-wrap align-items-center gap-2">
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" class="hidden" :ref="(el) => setMatFileRef(mat._key, el)" @change="(e) => onMatFile(e, mat)" />
            <ButtonPrime label="Adjuntar imagen" icon="pi pi-upload" class="p-button-outlined p-button-sm" @click="triggerMatFile(mat._key)" />
            <img v-if="mat.preview" :src="mat.preview" alt="" class="act-manual-form__thumb" />
            <CheckBox v-model="mat.obligatorio" :input-id="`mat-ob-${mat._key}`" :binary="true" />
            <label :for="`mat-ob-${mat._key}`" class="text-sm cursor-pointer">Obligatorio</label>
          </div>
        </div>
      </div>
    </section>

    <div class="flex flex-wrap gap-2 act-manual-form__footer">
      <ButtonPrime
        :label="botonGuardarLabel"
        icon="pi pi-save"
        :loading="guardando"
        @click="guardarTodo"
      />
      <ButtonPrime
        v-if="isEditMode"
        label="Nueva actividad"
        icon="pi pi-plus"
        class="p-button-outlined"
        :disabled="guardando"
        @click="iniciarNueva"
      />
      <ButtonPrime v-if="!embedded" label="Cancelar" class="p-button-outlined" @click="emit('cancel')" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import CheckBox from 'primevue/checkbox';
import RadioButton from 'primevue/radiobutton';
import ButtonPrime from 'primevue/button';
import FileUpload from 'primevue/fileupload';
import EstudyooLoadingBanner from '@/components/estudyoo/EstudyooLoadingBanner.vue';
import { ActividadesApi, descargarBlob } from '@/services/EstudyooAdminApi';
import { handleDjangoApiError } from '@/services/djangoAdminAuth';
import { compararCursos, labelCurso, NIVEL_OPTS } from '@/utils/nivelesEscolares';

defineProps({
  embedded: { type: Boolean, default: false },
  showExcelBar: { type: Boolean, default: true },
});

const emit = defineEmits(['created', 'updated', 'unassigned', 'cancel', 'imported']);

const toast = useToast();
const editActividadId = ref(null);
const editAsignacionId = ref(null);
const asignacionesCargadas = ref([]);
const cargandoEdit = ref(false);
const quitandoAsig = ref(false);
const materias = ref([]);
const tipos = ref([]);
const cursos = ref([]);
const casas = ref([]);
const capitulos = ref([]);
const loadingCap = ref(false);
const guardando = ref(false);
const descFull = ref(false);
const descMat = ref(false);
const importandoFull = ref(false);
const importandoMat = ref(false);
const matCapFileRef = ref(null);
const altFileRefs = ref({});
const matFileRefs = ref({});
const preguntaFileRefs = ref({});
const matchImgFileRefs = ref({});

const ctx = ref({ materia: null, nivel: null, curso: null, capitulo: null, tipo: null, casa: null });
const act = ref({ titulo: '', subtitulo: '', instrucciones: '', activa: true, modo: 'practica' });
const asig = ref({ activa: true, publicar: true });
const matCap = ref({ titulo: '', file: null, preview: '' });
const preguntas = ref([]);
const materiales = ref([]);
/** Tope alineado a generación con IA (dinámico, no fijo en 5). */
const MAX_PREGUNTAS_ACTIVIDAD = 20;
const cantidadPreguntasObjetivo = ref(5);

const isEditMode = computed(() => !!editActividadId.value);

const totalAsignacionesExtra = computed(() => Math.max(0, (asignacionesCargadas.value?.length || 0) - 1));

const botonGuardarLabel = computed(() => {
  if (isEditMode.value) return 'Guardar cambios';
  if (asig.value.publicar === false) return 'Guardar actividad (sin asignar)';
  return 'Guardar actividad + asignación';
});

const nivelOpts = NIVEL_OPTS;
const modoOpts = ['practica', 'evaluacion'];
const tipoPreguntaOpts = [
  { label: 'Alternativas con imagen', value: 'image_alternatives' },
  { label: 'Verdadero / falso', value: 'image_true_false' },
  { label: 'Ordenar palabras', value: 'order_words' },
  { label: 'Emparejar columnas', value: 'match_eight' },
  { label: 'Trazar patrón', value: 'trace_pattern' },
  { label: 'Imagen ↔ frase', value: 'match_images_phrases' },
  { label: 'Fracción con color', value: 'color_fraction' },
];
const tipoMaterialOpts = [
  { label: 'Texto', value: 'texto' },
  { label: 'Video', value: 'video' },
  { label: 'PDF', value: 'pdf' },
  { label: 'Enlace', value: 'enlace' },
  { label: 'Imagen', value: 'imagen' },
  { label: 'Otro', value: 'otro' },
];

function usaAlternativas(tipo) {
  return tipo === 'image_alternatives' || tipo === 'image_true_false';
}

function camposTipoDefault() {
  return {
    palabras: [],
    palabraNueva: '',
    matchIzq: ['', '', '', ''],
    matchDer: ['', '', '', ''],
    trazoGuia: 'A',
    anchoLienzo: 360,
    altoLienzo: 260,
    frases: [
      { id: 'F1', texto: '' },
      { id: 'F2', texto: '' },
      { id: 'F3', texto: '' },
    ],
    imagenes: [
      { id: 'I1', etiqueta: '', imagenUrl: '', file: null, preview: '' },
      { id: 'I2', etiqueta: '', imagenUrl: '', file: null, preview: '' },
      { id: 'I3', etiqueta: '', imagenUrl: '', file: null, preview: '' },
    ],
    filas: 2,
    columnas: 2,
    fraccionNumerador: 1,
    fraccionDenominador: 4,
    icono: 'help',
    imagenUrl: '',
    imagenFile: null,
    imagenPreview: '',
    _uploadingImg: false,
    respuestaCorrecta: true,
  };
}

function onTipoPreguntaChange(p) {
  if (p.tipo === 'image_true_false') {
    if (!(p.alternativas[0]?.texto || '').trim()) p.alternativas[0].texto = 'Verdadero';
    if (!(p.alternativas[1]?.texto || '').trim()) p.alternativas[1].texto = 'Falso';
    p.alternativas[2].texto = '';
    p.alternativas[3].texto = '';
  }
}

function agregarPalabra(p) {
  const w = (p.palabraNueva || '').trim();
  if (!w) return;
  p.palabras.push(w);
  p.palabraNueva = '';
}

function coerceDatosExtra(raw, tipo) {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return coerceDatosExtra(JSON.parse(raw), tipo);
    } catch {
      return {};
    }
  }
  if (Array.isArray(raw)) {
    if (tipo === 'order_words') return { palabras: raw };
    return {};
  }
  if (typeof raw === 'object') return { ...raw };
  return {};
}

function hydrateCamposTipo(p, datos, tipo) {
  const d = coerceDatosExtra(datos, tipo);
  if (tipo === 'order_words') {
    const palabras = Array.isArray(d.palabras) ? d.palabras.map((x) => String(x)) : [];
    p.palabras = palabras;
  } else if (tipo === 'match_eight') {
    const izq = d.columnaIzquierda || d.izquierda || [];
    const der = d.columnaDerecha || d.derecha || [];
    p.matchIzq = (izq.length ? izq : ['', '', '', '']).map((x) => (typeof x === 'object' ? (x.texto || '') : String(x || '')));
    p.matchDer = (der.length ? der : ['', '', '', '']).map((x) => (typeof x === 'object' ? (x.texto || '') : String(x || '')));
    while (p.matchIzq.length < 2) p.matchIzq.push('');
    while (p.matchDer.length < 2) p.matchDer.push('');
  } else if (tipo === 'trace_pattern') {
    p.trazoGuia = d.trazoGuia != null ? String(d.trazoGuia) : 'A';
    p.anchoLienzo = Number(d.anchoLienzo) || 360;
    p.altoLienzo = Number(d.altoLienzo) || 260;
  } else if (tipo === 'match_images_phrases') {
    const frases = Array.isArray(d.frases) ? d.frases : [];
    const imagenes = Array.isArray(d.imagenes) ? d.imagenes : [];
    p.frases = (frases.length ? frases : [{ id: 'F1', texto: '' }]).map((f, i) => ({
      id: f.id || `F${i + 1}`,
      texto: f.texto || f.frase || '',
    }));
    p.imagenes = (imagenes.length ? imagenes : [{ id: 'I1', etiqueta: '', imagenUrl: '' }]).map((im, i) => ({
      id: im.id || `I${i + 1}`,
      etiqueta: im.etiqueta || im.texto || '',
      imagenUrl: im.imagenUrl || im.url || '',
      file: null,
      preview: im.imagenUrl || im.url || '',
    }));
  } else if (tipo === 'color_fraction') {
    p.filas = Number(d.filas) || 2;
    p.columnas = Number(d.columnas) || 2;
    p.fraccionNumerador = Number(d.fraccionNumerador ?? d.numerador) || 1;
    p.fraccionDenominador = Number(d.fraccionDenominador ?? d.denominador) || 4;
  } else if (tipo === 'image_true_false') {
    p.icono = d.icono || 'help';
    p.imagenUrl = d.imagenUrl || '';
    p.imagenPreview = d.imagenUrl || '';
    p.imagenFile = null;
    if (typeof d.respuestaCorrecta === 'boolean') p.respuestaCorrecta = d.respuestaCorrecta;
  }
}

function buildDatosExtra(p) {
  if (p.tipo === 'order_words') {
    const palabras = (p.palabras || []).map((w) => String(w).trim()).filter(Boolean);
    return {
      palabras,
      ordenCorrectoIds: palabras.map((_, i) => i),
    };
  }
  if (p.tipo === 'match_eight') {
    const izqTxt = (p.matchIzq || []).map((t) => String(t || '').trim());
    const derTxt = (p.matchDer || []).map((t) => String(t || '').trim());
    const columnaIzquierda = [];
    const columnaDerecha = [];
    const paresCorrectos = [];
    for (let i = 0; i < Math.max(izqTxt.length, derTxt.length); i += 1) {
      if (!izqTxt[i] && !derTxt[i]) continue;
      const lid = `L${i + 1}`;
      const rid = `R${i + 1}`;
      if (izqTxt[i]) columnaIzquierda.push({ id: lid, texto: izqTxt[i] });
      if (derTxt[i]) columnaDerecha.push({ id: rid, texto: derTxt[i] });
      if (izqTxt[i] && derTxt[i]) paresCorrectos.push([lid, rid]);
    }
    return { columnaIzquierda, columnaDerecha, paresCorrectos };
  }
  if (p.tipo === 'trace_pattern') {
    return {
      trazoGuia: String(p.trazoGuia || 'A').slice(0, 2),
      anchoLienzo: Number(p.anchoLienzo) || 360,
      altoLienzo: Number(p.altoLienzo) || 260,
    };
  }
  if (p.tipo === 'match_images_phrases') {
    const frases = (p.frases || [])
      .map((f, i) => ({ id: f.id || `F${i + 1}`, texto: String(f.texto || '').trim() }))
      .filter((f) => f.texto);
    const imagenes = (p.imagenes || [])
      .map((im, i) => ({
        id: im.id || `I${i + 1}`,
        etiqueta: String(im.etiqueta || '').trim(),
        imagenUrl: im.imagenUrl || null,
      }))
      .filter((im) => im.etiqueta || im.imagenUrl);
    const n = Math.min(frases.length, imagenes.length);
    const paresCorrectos = [];
    for (let i = 0; i < n; i += 1) paresCorrectos.push([imagenes[i].id, frases[i].id]);
    return { frases, imagenes, paresCorrectos };
  }
  if (p.tipo === 'color_fraction') {
    return {
      filas: Number(p.filas) || 1,
      columnas: Number(p.columnas) || 1,
      fraccionNumerador: Number(p.fraccionNumerador) || 0,
      fraccionDenominador: Number(p.fraccionDenominador) || 1,
    };
  }
  if (p.tipo === 'image_true_false') {
    return {
      icono: p.icono || 'help',
      imagenUrl: p.imagenUrl || null,
      respuestaCorrecta: p.correctaIdx === 0,
    };
  }
  return {};
}

const excelBannerMsg = computed(() => {
  if (importandoFull.value) return 'Importando actividades desde Excel…';
  if (importandoMat.value) return 'Importando materiales…';
  if (descFull.value) return 'Descargando plantilla actividades…';
  if (descMat.value) return 'Descargando plantilla materiales…';
  return '';
});

let keySeq = 0;
function nextKey() {
  keySeq += 1;
  return `k${keySeq}`;
}

const cursoOpts = computed(() => {
  let list = cursos.value || [];
  if (ctx.value.nivel) {
    list = list.filter((c) => (c.nivel_simce || '') === ctx.value.nivel);
  }
  return list.map((c) => ({ ...c, label: labelCurso(c) })).sort(compararCursos);
});

const capituloOpts = computed(() =>
  (capitulos.value || []).map((c) => ({
    ...c,
    label: `${c.nombre || c.codigo}${c.nivel_simce ? ` · ${c.nivel_simce}` : ''}`,
  })),
);

const casaOpts = computed(() => [
  { id: null, label: 'Todo el curso' },
  ...(casas.value || []).map((c) => ({
    id: c.id,
    label: `${c.codigo || ''}${c.nombre ? ` — ${c.nombre}` : ''}`,
  })),
]);

function onAsigPublicarChange() {
  if (asig.value.publicar === false) {
    asig.value.activa = false;
  }
}

async function quitarAsignacion() {
  if (!editAsignacionId.value) return;
  const titulo = act.value.titulo || 'esta actividad';
  const cursoLbl = ctx.value.curso ? labelCurso(ctx.value.curso) : 'el curso';
  if (!window.confirm(`¿Quitar la asignación de «${titulo}» de ${cursoLbl}?`)) return;
  const asigId = editAsignacionId.value;
  const actId = editActividadId.value;
  quitandoAsig.value = true;
  try {
    await ActividadesApi.deleteAsignacion(asigId);
    editAsignacionId.value = null;
    asig.value.publicar = false;
    asig.value.activa = false;
    asignacionesCargadas.value = asignacionesCargadas.value.filter((a) => a.id !== asigId);
    toast.add({
      severity: 'info',
      summary: 'Asignación eliminada',
      detail: 'La actividad sigue en el catálogo; ya no está publicada al curso.',
      life: 5000,
    });
    emit('unassigned', { actividad_id: actId });
  } catch (e) {
    djangoCatch(e, 'Quitar asignación');
  } finally {
    quitandoAsig.value = false;
  }
}

function colegioIdActual() {
  return ctx.value.curso?.colegio_id || ctx.value.materia?.colegio_id || null;
}

function djangoCatch(e, summary = 'Error') {
  if (!handleDjangoApiError(e, { toast })) {
    toast.add({
      severity: 'error',
      summary,
      detail: e?.response?.data?.detail || e?.message || 'Error inesperado',
      life: 6000,
    });
  }
}

function previewFile(file, target) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (e) => {
    target.preview = e.target?.result || '';
  };
  reader.readAsDataURL(file);
}

function onMatCapFile(ev) {
  const file = ev.target.files?.[0];
  if (!file) return;
  matCap.value.file = file;
  previewFile(file, matCap.value);
  ev.target.value = '';
}

function setAltFileRef(pKey, j, el) {
  if (el) altFileRefs.value[`${pKey}-${j}`] = el;
}

function triggerAltFile(pKey, j) {
  altFileRefs.value[`${pKey}-${j}`]?.click();
}

function onAltFile(ev, p, j) {
  const file = ev.target.files?.[0];
  if (!file) return;
  p.alternativas[j].file = file;
  previewFile(file, p.alternativas[j]);
  ev.target.value = '';
}

function setPreguntaFileRef(key, el) {
  if (el) preguntaFileRefs.value[key] = el;
}

function triggerPreguntaFile(key) {
  preguntaFileRefs.value[key]?.click();
}

function onPreguntaImagenFile(ev, p) {
  const file = ev.target.files?.[0];
  if (!file) return;
  p.imagenFile = file;
  const reader = new FileReader();
  reader.onload = () => {
    p.imagenPreview = String(reader.result || '');
  };
  reader.readAsDataURL(file);
  ev.target.value = '';
}

function limpiarImagenPregunta(p) {
  p.imagenFile = null;
  p.imagenPreview = '';
  p.imagenUrl = '';
}

function setMatchImgFileRef(key, i, el) {
  if (el) matchImgFileRefs.value[`${key}-${i}`] = el;
}

function triggerMatchImgFile(key, i) {
  matchImgFileRefs.value[`${key}-${i}`]?.click();
}

function onMatchImgFile(ev, p, i) {
  const file = ev.target.files?.[0];
  if (!file) return;
  const im = p.imagenes[i];
  im.file = file;
  previewFile(file, im);
  ev.target.value = '';
}

function setMatFileRef(key, el) {
  if (el) matFileRefs.value[key] = el;
}

function triggerMatFile(key) {
  matFileRefs.value[key]?.click();
}

function onMatFile(ev, mat) {
  const file = ev.target.files?.[0];
  if (!file) return;
  mat.file = file;
  mat.tipo = 'imagen';
  previewFile(file, mat);
  ev.target.value = '';
}

async function bootstrap() {
  const [m, t, c, ca] = await Promise.all([
    ActividadesApi.listMaterias(),
    ActividadesApi.listTipos(),
    ActividadesApi.listCursos(),
    ActividadesApi.listCasas(),
  ]);
  materias.value = m;
  tipos.value = t;
  cursos.value = c;
  casas.value = ca;
  if (!preguntas.value.length) agregarPregunta();
}

onMounted(() => {
  bootstrap().catch((e) => djangoCatch(e, 'Catálogo'));
});

async function cargarCapitulos() {
  if (!ctx.value.materia?.id) {
    capitulos.value = [];
    return;
  }
  loadingCap.value = true;
  try {
    const params = { materia_id: ctx.value.materia.id };
    if (ctx.value.nivel) params.nivel_simce = ctx.value.nivel;
    capitulos.value = await ActividadesApi.listCapitulos(params);
  } finally {
    loadingCap.value = false;
  }
}

function onMateriaChange() {
  ctx.value.capitulo = null;
  cargarCapitulos();
}

function onNivelChange() {
  ctx.value.curso = null;
  ctx.value.capitulo = null;
  cargarCapitulos();
}

function nuevaPregunta() {
  return {
    _key: nextKey(),
    tipo: 'image_alternatives',
    enunciado: '',
    explicacion: '',
    orden: preguntas.value.length,
    correctaIdx: 0,
    alternativas: [
      { texto: '', file: null, preview: '' },
      { texto: '', file: null, preview: '' },
      { texto: '', file: null, preview: '' },
      { texto: '', file: null, preview: '' },
    ],
    ...camposTipoDefault(),
  };
}

function agregarPregunta() {
  if (preguntas.value.length >= MAX_PREGUNTAS_ACTIVIDAD) {
    toast.add({
      severity: 'warn',
      summary: 'Límite',
      detail: `Máximo ${MAX_PREGUNTAS_ACTIVIDAD} preguntas por actividad.`,
      life: 4000,
    });
    return;
  }
  preguntas.value.push(nuevaPregunta());
  cantidadPreguntasObjetivo.value = preguntas.value.length;
}

/**
 * Ajusta el número de preguntas al valor del campo cantidad (1–20).
 * Si sube: agrega slots vacíos. Si baja: elimina desde el final (con confirmación si ya existen en BD).
 */
async function aplicarCantidadPreguntas() {
  let target = Number(cantidadPreguntasObjetivo.value);
  if (!Number.isFinite(target)) target = 1;
  target = Math.min(MAX_PREGUNTAS_ACTIVIDAD, Math.max(1, Math.round(target)));
  cantidadPreguntasObjetivo.value = target;

  const actual = preguntas.value.length;
  if (target === actual) {
    toast.add({
      severity: 'info',
      summary: 'Cantidad',
      detail: `Ya hay ${actual} pregunta(s).`,
      life: 3000,
    });
    return;
  }

  if (target > actual) {
    const tipoDefault = ctx.value.tipo?.codigo || 'image_alternatives';
    while (preguntas.value.length < target) {
      const p = nuevaPregunta();
      if (tipoDefault && tipoPreguntaOpts.some((o) => o.value === tipoDefault)) {
        p.tipo = tipoDefault;
        onTipoPreguntaChange(p);
      }
      preguntas.value.push(p);
    }
    toast.add({
      severity: 'success',
      summary: 'Preguntas',
      detail: `Se agregaron ${target - actual} pregunta(s). Completa enunciados y guarda.`,
      life: 5000,
    });
    return;
  }

  const aQuitar = preguntas.value.slice(target);
  const conId = aQuitar.filter((p) => p.id);
  if (conId.length) {
    const ok = window.confirm(
      `Vas a quitar ${aQuitar.length} pregunta(s) del final (${conId.length} ya guardada(s) se eliminarán). ¿Continuar?`,
    );
    if (!ok) {
      cantidadPreguntasObjetivo.value = actual;
      return;
    }
  }
  for (let i = preguntas.value.length - 1; i >= target; i -= 1) {
    const p = preguntas.value[i];
    if (p?.id) {
      try {
        await ActividadesApi.deletePregunta(p.id);
      } catch (e) {
        djangoCatch(e, 'Eliminar pregunta');
        cantidadPreguntasObjetivo.value = preguntas.value.length;
        return;
      }
    }
    preguntas.value.splice(i, 1);
  }
  toast.add({
    severity: 'success',
    summary: 'Preguntas',
    detail: `Quedaron ${preguntas.value.length} pregunta(s).`,
    life: 4000,
  });
}

function quitarPregunta(idx) {
  quitarPreguntaAsync(idx);
}

async function quitarPreguntaAsync(idx) {
  const p = preguntas.value[idx];
  if (p?.id) {
    try {
      await ActividadesApi.deletePregunta(p.id);
    } catch (e) {
      djangoCatch(e, 'Eliminar pregunta');
      return;
    }
  }
  preguntas.value.splice(idx, 1);
  if (!preguntas.value.length) agregarPregunta();
  cantidadPreguntasObjetivo.value = Math.max(1, preguntas.value.length);
}

function agregarMaterial() {
  materiales.value.push({
    _key: nextKey(),
    titulo: '',
    tipo: 'texto',
    contenido: '',
    url: '',
    file: null,
    preview: '',
    obligatorio: true,
    orden: materiales.value.length,
  });
}

function quitarMaterial(idx) {
  quitarMaterialAsync(idx);
}

async function quitarMaterialAsync(idx) {
  const mat = materiales.value[idx];
  if (mat?.id) {
    try {
      await ActividadesApi.deleteMaterialActividad(mat.id);
    } catch (e) {
      djangoCatch(e, 'Eliminar material');
      return;
    }
  }
  materiales.value.splice(idx, 1);
}

function validar() {
  if (!ctx.value.materia?.id) return 'Elige una materia.';
  if (!ctx.value.capitulo?.id) return 'Elige un capítulo para la actividad.';
  if (!ctx.value.tipo?.id) return 'Elige un tipo de actividad.';
  if (asig.value.publicar !== false && !ctx.value.curso?.id) {
    return 'Elige el curso o desmarca «Asignar al curso seleccionado».';
  }
  if (!(act.value.titulo || '').trim()) return 'El título es obligatorio.';
  if (!preguntas.value.length) return 'Agrega al menos una pregunta.';
  for (let i = 0; i < preguntas.value.length; i += 1) {
    const p = preguntas.value[i];
    const n = i + 1;
    if (!(p.enunciado || '').trim()) return `La pregunta ${n} necesita enunciado.`;
    if (p.tipo === 'order_words' && (p.palabras || []).filter((w) => String(w).trim()).length < 2) {
      return `La pregunta ${n} (ordenar palabras) necesita al menos 2 palabras.`;
    }
    if (p.tipo === 'match_eight') {
      const izq = (p.matchIzq || []).filter((t) => String(t).trim());
      const der = (p.matchDer || []).filter((t) => String(t).trim());
      if (izq.length < 2 || der.length < 2) {
        return `La pregunta ${n} (emparejar) necesita al menos 2 pares izquierda/derecha.`;
      }
    }
    if (p.tipo === 'trace_pattern' && !(p.trazoGuia || '').trim()) {
      return `La pregunta ${n} (trazo) necesita letra o número guía.`;
    }
    if (p.tipo === 'match_images_phrases') {
      const nf = (p.frases || []).filter((f) => (f.texto || '').trim()).length;
      const ni = (p.imagenes || []).filter((im) => (im.etiqueta || '').trim() || im.imagenUrl).length;
      if (nf < 2 || ni < 2) return `La pregunta ${n} (imagen↔frase) necesita al menos 2 pares.`;
    }
    if (p.tipo === 'color_fraction') {
      const den = Number(p.fraccionDenominador) || 0;
      const celdas = (Number(p.filas) || 0) * (Number(p.columnas) || 0);
      if (den < 1 || celdas !== den) {
        return `La pregunta ${n} (fracción): filas×columnas debe igualar el denominador (${den || '?'}).`;
      }
    }
    if (usaAlternativas(p.tipo)) {
      const filled = (p.alternativas || []).filter((a) => (a.texto || '').trim() || a.file || a.preview);
      if (filled.length < 2) return `La pregunta ${n} necesita al menos 2 alternativas.`;
    }
  }
  return null;
}

function resetForm(preserveCtx = true) {
  const savedCtx = preserveCtx
    ? {
        materia: ctx.value.materia,
        nivel: ctx.value.nivel,
        curso: ctx.value.curso,
        capitulo: ctx.value.capitulo,
        tipo: ctx.value.tipo,
        casa: ctx.value.casa,
      }
    : {
        materia: null,
        nivel: null,
        curso: null,
        capitulo: null,
        tipo: null,
        casa: null,
      };

  editActividadId.value = null;
  editAsignacionId.value = null;
  asignacionesCargadas.value = [];
  ctx.value = { ...savedCtx };
  act.value = { titulo: '', subtitulo: '', instrucciones: '', activa: true, modo: 'practica' };
  asig.value = { activa: true, publicar: true };
  matCap.value = { titulo: '', file: null, preview: '' };
  materiales.value = [];
  preguntas.value = [];
  cantidadPreguntasObjetivo.value = 5;
  agregarPregunta();
}

function iniciarNueva() {
  resetForm(true);
  toast.add({
    severity: 'info',
    summary: 'Nueva actividad',
    detail: 'Formulario listo. Se mantuvo materia, capítulo y curso.',
    life: 4000,
  });
}

function altSlotsFromList(alts) {
  const sorted = [...(alts || [])].sort((a, b) => (a.orden || 0) - (b.orden || 0));
  const correctaIdx = Math.max(0, sorted.findIndex((a) => a.es_correcta));
  const mapped = sorted.map((alt) => ({
    id: alt.id,
    texto: alt.texto || '',
    file: null,
    preview: alt.imagen_url || '',
  }));
  while (mapped.length < 4) {
    mapped.push({ texto: '', file: null, preview: '' });
  }
  return { alternativas: mapped.slice(0, 4), correctaIdx };
}

async function loadActividad(row) {
  if (!row?.id) return;
  cargandoEdit.value = true;
  try {
    if (!materias.value.length) await bootstrap();

    const actividad = await ActividadesApi.getActividad(row.id);
    editActividadId.value = actividad.id;

    act.value = {
      titulo: actividad.titulo || '',
      subtitulo: actividad.subtitulo || '',
      instrucciones: actividad.instrucciones || '',
      activa: actividad.activa !== false,
      modo: actividad.datos_configuracion?.modo || 'practica',
    };

    ctx.value.materia = materias.value.find((m) => m.id === actividad.materia) || null;
    ctx.value.tipo = tipos.value.find((t) => t.id === actividad.tipo) || null;
    ctx.value.nivel = null;
    ctx.value.capitulo = null;
    ctx.value.curso = null;
    ctx.value.casa = null;

    if (ctx.value.materia) {
      await cargarCapitulos();
      ctx.value.capitulo = capitulos.value.find((c) => c.id === actividad.capitulo) || null;
      if (ctx.value.capitulo?.nivel_simce) ctx.value.nivel = ctx.value.capitulo.nivel_simce;
    }

    const asigs = await ActividadesApi.listAsignaciones({ actividad_id: actividad.id });
    asignacionesCargadas.value = asigs;
    if (asigs.length) {
      const asignacion = asigs[0];
      editAsignacionId.value = asignacion.id;
      asig.value.activa = asignacion.activa !== false;
      asig.value.publicar = true;
      ctx.value.curso = cursos.value.find((c) => c.id === asignacion.curso) || null;
      if (asignacion.casa) {
        ctx.value.casa = casas.value.find((c) => c.id === asignacion.casa) || null;
      }
    } else {
      editAsignacionId.value = null;
      asig.value = { activa: true, publicar: true };
    }

    const pregs = await ActividadesApi.listPreguntasActividad({ actividad_id: actividad.id });
    preguntas.value = [];
    for (const pg of [...pregs].sort((a, b) => (a.orden || 0) - (b.orden || 0))) {
      const item = {
        id: pg.id,
        _key: nextKey(),
        tipo: pg.tipo,
        enunciado: pg.enunciado || '',
        explicacion: pg.explicacion || '',
        orden: pg.orden ?? 0,
        correctaIdx: 0,
        alternativas: [
          { texto: '', file: null, preview: '' },
          { texto: '', file: null, preview: '' },
          { texto: '', file: null, preview: '' },
          { texto: '', file: null, preview: '' },
        ],
        ...camposTipoDefault(),
      };
      hydrateCamposTipo(item, pg.datos_extra, pg.tipo);
      if (usaAlternativas(pg.tipo)) {
        const alts = await ActividadesApi.listAlternativas({ pregunta_id: pg.id });
        const slots = altSlotsFromList(alts);
        item.alternativas = slots.alternativas;
        item.correctaIdx = slots.correctaIdx;
        if (pg.tipo === 'image_true_false' && typeof item.respuestaCorrecta === 'boolean') {
          item.correctaIdx = item.respuestaCorrecta ? 0 : 1;
        }
      }
      preguntas.value.push(item);
    }
    if (!preguntas.value.length) agregarPregunta();
    cantidadPreguntasObjetivo.value = Math.min(
      MAX_PREGUNTAS_ACTIVIDAD,
      Math.max(1, preguntas.value.length),
    );

    const mats = await ActividadesApi.listMaterialesActividad({ actividad_id: actividad.id });
    materiales.value = [...mats]
      .sort((a, b) => (a.orden || 0) - (b.orden || 0))
      .map((mat) => ({
        id: mat.id,
        _key: nextKey(),
        titulo: mat.titulo || '',
        tipo: mat.tipo || 'texto',
        contenido: mat.contenido || '',
        url: mat.url || '',
        file: null,
        preview: mat.tipo === 'imagen' && mat.url ? mat.url : '',
        obligatorio: mat.obligatorio !== false,
        orden: mat.orden ?? 0,
      }));

    matCap.value = { titulo: '', file: null, preview: '' };
  } catch (e) {
    djangoCatch(e, 'Cargar actividad');
  } finally {
    cargandoEdit.value = false;
  }
}

function payloadActividad() {
  return {
    materia_id: ctx.value.materia.id,
    capitulo_id: ctx.value.capitulo.id,
    tipo_id: ctx.value.tipo.id,
    titulo: act.value.titulo.trim(),
    subtitulo: act.value.subtitulo || '',
    instrucciones: act.value.instrucciones || '',
    activa: act.value.activa !== false,
    datos_configuracion: { modo: act.value.modo || 'practica' },
  };
}

async function syncPreguntasYAlternativas(actividadId) {
  for (const p of preguntas.value) {
    // Subir archivos de imagen↔frase antes de armar datos_extra finales
    if (p.tipo === 'match_images_phrases') {
      for (let i = 0; i < (p.imagenes || []).length; i += 1) {
        const im = p.imagenes[i];
        if (!im.file) continue;
        const up = await ActividadesApi.subirImagenAsset(im.file);
        im.imagenUrl = up.url;
        im.preview = up.url;
        im.file = null;
      }
    }
    if (p.tipo === 'image_true_false' && p.imagenFile) {
      const up = await ActividadesApi.subirImagenAsset(p.imagenFile);
      p.imagenUrl = up.url;
      p.imagenPreview = up.url;
      p.imagenFile = null;
    }

    const datosExtra = buildDatosExtra(p);
    const base = {
      actividad_id: actividadId,
      tipo: p.tipo,
      enunciado: p.enunciado.trim(),
      explicacion: p.explicacion || '',
      orden: p.orden ?? 0,
      activa: true,
      datos_extra: datosExtra,
    };
    const pregunta = p.id
      ? await ActividadesApi.patchPregunta(p.id, base)
      : await ActividadesApi.createPregunta(base);

    if (usaAlternativas(p.tipo)) {
      for (let j = 0; j < p.alternativas.length; j += 1) {
        const alt = p.alternativas[j];
        const texto = (alt.texto || '').trim();
        if (!texto && !alt.file && !alt.id) continue;
        const altPayload = {
          pregunta_id: pregunta.id,
          texto: texto || `Opción ${String.fromCharCode(65 + j)}`,
          es_correcta: p.correctaIdx === j,
          orden: j + 1,
        };
        const creada = alt.id
          ? await ActividadesApi.patchAlternativa(alt.id, altPayload)
          : await ActividadesApi.createAlternativa(altPayload);
        if (alt.file) {
          await ActividadesApi.subirImagenMaterial('alternativa', creada.id, alt.file);
          alt.file = null;
        }
        if (!alt.id) alt.id = creada.id;
      }
    }
    if (!p.id) p.id = pregunta.id;
  }
}

async function syncMateriales(actividadId) {
  for (const mat of materiales.value) {
    if (!(mat.titulo || '').trim() && !mat.file && !mat.id) continue;
    const payload = {
      actividad_id: actividadId,
      titulo: (mat.titulo || 'Material').trim(),
      tipo: mat.file ? 'imagen' : mat.tipo || 'texto',
      contenido: mat.contenido || '',
      url: mat.url || '',
      orden: mat.orden ?? 0,
      obligatorio: mat.obligatorio !== false,
      activo: true,
    };
    const creado = mat.id
      ? await ActividadesApi.patchMaterialActividad(mat.id, payload)
      : await ActividadesApi.crearMaterialActividad(payload);
    if (mat.file) {
      await ActividadesApi.subirImagenMaterial('material_actividad', creado.id, mat.file);
      mat.file = null;
    }
    if (!mat.id) mat.id = creado.id;
  }
}

async function syncAsignacion(actividadId) {
    if (asig.value.publicar === false) {
    if (editAsignacionId.value) {
      const asigId = editAsignacionId.value;
      await ActividadesApi.deleteAsignacion(asigId);
      asignacionesCargadas.value = asignacionesCargadas.value.filter((a) => a.id !== asigId);
      editAsignacionId.value = null;
    }
    return;
  }
  if (!ctx.value.curso?.id) return;
  const payload = {
    actividad_id: actividadId,
    curso_id: ctx.value.curso.id,
    materia_id: ctx.value.materia.id,
    casa_id: ctx.value.casa?.id || null,
    activa: asig.value.activa !== false,
  };
  if (editAsignacionId.value) {
    await ActividadesApi.patchAsignacion(editAsignacionId.value, payload);
  } else {
    const creada = await ActividadesApi.createAsignacion(payload);
    editAsignacionId.value = creada.id;
    asignacionesCargadas.value = [...asignacionesCargadas.value, creada];
  }
}

async function guardarTodo() {
  const err = validar();
  if (err) {
    toast.add({ severity: 'warn', summary: 'Revisa el formulario', detail: err, life: 5000 });
    return;
  }
  guardando.value = true;
  try {
    if (!isEditMode.value && matCap.value.titulo?.trim() && ctx.value.capitulo?.id) {
      const mc = await ActividadesApi.crearMaterialCapitulo({
        capitulo_id: ctx.value.capitulo.id,
        titulo: matCap.value.titulo.trim(),
        tipo: matCap.value.file ? 'imagen' : 'texto',
        contenido: '',
        url: '',
        orden: 0,
        activo: true,
      });
      if (matCap.value.file) {
        await ActividadesApi.subirImagenMaterial('material_capitulo', mc.id, matCap.value.file);
      }
    }

    const actividad = isEditMode.value
      ? await ActividadesApi.patchActividad(editActividadId.value, payloadActividad())
      : await ActividadesApi.createActividad(payloadActividad());

    await syncPreguntasYAlternativas(actividad.id);
    await syncMateriales(actividad.id);
    await syncAsignacion(actividad.id);

    const tituloGuardado = act.value.titulo.trim();
    const fueEdicion = isEditMode.value;

    toast.add({
      severity: 'success',
      summary: fueEdicion ? 'Actividad actualizada' : 'Actividad creada',
      detail: `${fueEdicion ? 'Cambios guardados' : 'Actividad creada'} en "${tituloGuardado}". Formulario listo para otra (misma materia, capítulo y curso).`,
      life: 6500,
    });

    emit(fueEdicion ? 'updated' : 'created', actividad);
    if (fueEdicion) {
      await loadActividad({ id: actividad.id });
    } else {
      resetForm(true);
    }
  } catch (e) {
    djangoCatch(e, isEditMode.value ? 'Actualizar actividad' : 'Guardar actividad');
  } finally {
    guardando.value = false;
  }
}

async function bajarPlantillaFull() {
  descFull.value = true;
  try {
    const res = await ActividadesApi.descargarPlantillaActividades();
    descargarBlob(res, 'plantilla-actividades-estudyoo.xlsx');
  } catch (e) {
    djangoCatch(e, 'Plantilla actividades');
  } finally {
    descFull.value = false;
  }
}

async function bajarPlantillaMat() {
  descMat.value = true;
  try {
    const res = await ActividadesApi.descargarPlantillaMateriales();
    descargarBlob(res, 'plantilla-materiales-estudyoo.xlsx');
  } catch (e) {
    djangoCatch(e, 'Plantilla materiales');
  } finally {
    descMat.value = false;
  }
}

async function importarFullExcel(ev) {
  const file = ev.files?.[0];
  if (!file) return;
  importandoFull.value = true;
  try {
    const r = await ActividadesApi.importarActividades(file, colegioIdActual());
    toast.add({
      severity: r.ok ? 'success' : 'warn',
      summary: 'Importación actividades',
      detail: r.ok ? 'Excel importado correctamente.' : `${(r.errores || []).length} error(es) — revisa consola.`,
      life: 7000,
    });
    emit('imported', r);
  } catch (e) {
    djangoCatch(e, 'Importación actividades');
  } finally {
    importandoFull.value = false;
    ev.options?.clear?.();
  }
}

async function importarMatExcel(ev) {
  const file = ev.files?.[0];
  if (!file) return;
  importandoMat.value = true;
  try {
    const r = await ActividadesApi.importarMateriales(file, colegioIdActual());
    toast.add({
      severity: r.ok ? 'success' : 'warn',
      summary: 'Importación materiales',
      detail: r.ok ? 'Materiales importados.' : `${(r.errores || []).length} error(es).`,
      life: 7000,
    });
    emit('imported', r);
  } catch (e) {
    djangoCatch(e, 'Importación materiales');
  } finally {
    importandoMat.value = false;
    ev.options?.clear?.();
  }
}

defineExpose({ loadActividad, iniciarNueva, resetForm });
</script>

<style scoped>
.act-manual-form__asig-box {
  background: var(--surface-50, #f8fafc);
}

.act-manual-form__block {
  background: var(--surface-50, #f8fafc);
}
.act-manual-form__thumb {
  max-height: 56px;
  max-width: 80px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--surface-border, #dee2e6);
}
.act-manual-form__thumb--sm {
  max-height: 40px;
  max-width: 56px;
}
.alt-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.preg-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background: #e0e7ff;
  color: #312e81;
  font-size: 0.85rem;
}
.preg-chip__x {
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  color: inherit;
  padding: 0 0.15rem;
}
.preg-img-box {
  padding: 0.75rem 0.85rem;
  border-radius: 8px;
  border: 1px dashed #94a3b8;
  background: #f8fafc;
}
.preg-cantidad-bar {
  background: #f1f5f9;
}
.act-manual-form__footer {
  position: sticky;
  bottom: 0;
  padding: 1rem 0;
  background: linear-gradient(transparent, var(--surface-ground, #fff) 35%);
  z-index: 2;
}
</style>
